<template>
<!--
  Local URL: http://localhost:8080/curate-vecdyn
  Required Group Membership: Curator or Admin
  Purpose: This page is for members of the Curators group. It features a table displaying previously uploaded 
  VecDyn files and their status. Clicking on a row in the table will take you to the detail page for that uploaded 
  file where you can see more information about the file, download the file and accept/reject the file.
-->
  <VDBaseTemplate>
      <!-- This modal shows when the curator clicks the "DELETE" link. -->
      <b-modal
        id="delete-dataset-modal"
        v-model="deleteDatasetModalShow"
        centered
        scrollable
        no-close-on-backdrop
        title="Confirm File Deletion"
        ok-title="Yes, Delete"
        @hidden="resetDeleteModal"
        @ok="handleDelete"
        :header-bg-variant="'danger'"
        :header-text-variant="'light'">
        <span v-html="`Are you sure you want to delete this file?`"></span>
        <form ref="form" @submit.stop.prevent="handleSubmit">
          <b-form-group
            invalid-feedback="Confirmation is required"
            :state="deleteConfirmState"
          >
          <br>
          <b-form-checkbox
            id="delete-confirm-input"
            name="delete-confirm-chkbx"
            v-model="deleteConfirmed"
          >I'm sure.</b-form-checkbox>
          </b-form-group>
        </form>
      </b-modal>
      <!-- This modal shows when the curator clicks the "COMMIT" link. -->
      <b-modal
        id="commit-modal"
        v-model="forceCommitModalShow"
        centered
        scrollable
        hide-footer
        hide-header-close
        no-close-on-backdrop
        no-close-on-esc
        title="Committing File"
        :header-bg-variant="'warning'"
        :header-text-variant="'light'">
        <span v-html="'Committing file to database. This may take several minutes.'"></span>
      </b-modal>
      <!-- This modal shows to confirm rollback. -->
      <b-modal
        id="confirm-rollback-modal"
        v-model="confirmRollbackModalShow"
        centered
        scrollable
        hide-footer
        hide-header-close
        no-close-on-backdrop
        no-close-on-esc
        title="Confirm Rollback"
        :header-bg-variant="'warning'"
        :header-text-variant="'light'">
        <span v-html="'Are you sure you want to rollback this file upload? This action cannot be undone.'"></span>
        <b-button class="mt-3" variant="warning" @click="executeRollback(); hideConfirmRollbackModal()" block>Rollback</b-button>
        <b-button class="mt-3" @click="hideConfirmRollbackModal" block>Cancel</b-button>
      </b-modal>
      <!-- This modal shows while the rollback is in progress. -->
      <b-modal
        id="rollback-working-modal"
        v-model="rollbackWorkingModalShow"
        centered
        scrollable
        hide-footer
        hide-header-close
        no-close-on-backdrop
        no-close-on-esc
        title="Rolling Back"
        :header-bg-variant="'warning'"
        :header-text-variant="'light'">
        <span v-html="'Please wait while the rollback is processed. This may take several minutes.'"></span>
      </b-modal>
      <!-- This modal shows when there is an error. -->
      <b-modal
            id="api-err-modal"
            v-model="apiErrModalShow"
            centered
            scrollable
            hide-footer
            hide-header-close
            no-close-on-backdrop
            no-close-on-esc
            title="Error"
            :header-bg-variant="'warning'"
            :header-text-variant="'light'"
        >
            <span v-html="err_msg"></span>
            <b-button class="mt-3" block @click="hideModal">OK</b-button>
        </b-modal>
      <!-- This modal shows upload errors for a file. -->
      <b-modal
            id="view-errors-modal"
            v-model="viewErrorsModalShow"
            centered
            scrollable
            size="lg"
            title="Upload Errors"
            :header-bg-variant="'info'"
            :header-text-variant="'light'"
            ok-only
            ok-title="Close"
        >
            <!-- Loading spinner while fetching errors -->
            <div v-if="viewErrorsLoading" class="text-center my-3">
              <b-spinner variant="primary"></b-spinner>
              <p class="mt-2">Loading errors...</p>
            </div>
            <!-- Error content once loaded -->
            <div v-else>
              <!-- File Details Section -->
              <div v-if="viewErrorsData.file_details" class="mb-3">
                <h5>File Details</h5>
                <b-table-simple small bordered>
                  <b-tbody>
                    <b-tr>
                      <b-td class="font-weight-bold" style="width: 150px;">File Name</b-td>
                      <b-td>{{ viewErrorsData.file_details.file_name }}</b-td>
                    </b-tr>
                    <b-tr>
                      <b-td class="font-weight-bold">Uploader</b-td>
                      <b-td>{{ viewErrorsData.file_details.uploader }}</b-td>
                    </b-tr>
                    <b-tr>
                      <b-td class="font-weight-bold">Status</b-td>
                      <b-td>{{ viewErrorsData.file_details.status }}</b-td>
                    </b-tr>
                    <b-tr v-if="viewErrorsData.file_details.notes">
                      <b-td class="font-weight-bold">Notes</b-td>
                      <b-td>{{ viewErrorsData.file_details.notes }}</b-td>
                    </b-tr>
                  </b-tbody>
                </b-table-simple>
              </div>
              <!-- Error List Section -->
              <div>
                <h5>Errors ({{ viewErrorsData.error_list ? viewErrorsData.error_list.length : 0 }})</h5>
                <div v-if="viewErrorsData.error_list && viewErrorsData.error_list.length > 0">
                  <b-list-group style="max-height: 300px; overflow-y: auto;">
                    <b-list-group-item 
                      v-for="(error, index) in viewErrorsData.error_list" 
                      :key="index"
                      variant="danger"
                    >
                      {{ error }}
                    </b-list-group-item>
                  </b-list-group>
                </div>
                <b-alert v-else show variant="success">
                  No errors found for this file.
                </b-alert>
              </div>
            </div>
        </b-modal>
    <div class="w-100 h-100 pb-4 mt-3">
      <b-container fluid class="pt-5 mt-3">
        <h2 class="tw-mb-0 text-aegis-dk">Curate VecDyn File Uploads</h2>
        <p class="tw-italic tw-text-gray-700">
          Files pending approval are displayed here.
        </p>
          <b-button v-b-toggle.collapse-instructions variant="primary">Instructions</b-button>
          <b-collapse id="collapse-instructions">
            <div style="padding:15px;">
              <p><b>Curating Files</b></p>
              <p>Approve or Reject files.</p>
            </div>
          </b-collapse>
        </b-container>
        <hr />

      <!-- FILE TABLE SECTION -->
      <b-container fluid>
        <div style="padding: 50px;">
          <h3>Uploaded VecDyn Files:</h3>
            <b-table
              ref="table"
              striped 
              hover 
              responsive
              selectable
              select-mode='single'
              bordered
              head-variant="light"
              style="overflow-x:auto"
              
              @row-selected="onRowSelected"
              :items="myProvider"
              :fields="fields"
              :busy.sync="loading"
              :current-page="options.currentPage"
              :per-page="options.perPage"
              :sort-by.sync="sortBy"
              :sort-desc.sync="sortDesc"

              tbody-tr-class="vt-table-row"
            >
              <template #cell(actions)="data">
                  <!-- <b-button v-if="!view_only" variant="outline-primary" @click="openDownloadModal(data.item)">Download</b-button> -->
                  <b-button variant="outline-primary" class="btn-xs" @click="rejectFile(data.item)">Reject</b-button>
                  <b-button variant="outline-danger" class="btn-xs" @click="showDeleteModal(data.item)">Delete</b-button>
                  <!-- <b-button
                    v-if="is_admin"
                    variant="outline-success" class="btn-xs" @click="forceCommitFile(data.item)"
                  >Commit</b-button> -->
                  <b-button
                    v-if="is_admin"
                    variant="outline-success" class="btn-xs" @click="naiveCommitFile(data.item)"
                  >Commit</b-button>
                  <b-button
                    v-if="is_admin"
                    variant="outline-warning" class="btn-xs" @click="showRollbackModal(data.item)"
                  >Roll Back</b-button>
                  <b-button
                    variant="outline-info" class="btn-xs" @click="viewErrors(data.item)"
                  >View Errors</b-button>
              </template>
              <!-- https://bootstrap-vue.org/docs/components/table#table-busy-state -->
              <template #table-busy>
                  <div class="text-danger my-2">
                      <b-spinner class="align-left"></b-spinner>
                      <strong>&nbsp;Loading...</strong>
                  </div>
              </template>

              <template v-slot:cell()="data"><!--Tooltip-->
                <span  :title='data.value'>{{ data.value}}</span>
              </template>
            </b-table>

            <b-pagination
                v-if="options.totalRows"
                v-model="options.currentPage"
                :total-rows="options.totalRows"
                :per-page="options.perPage"
                align="right"
                size="sm"
                class="my-0"
            ></b-pagination>

            <b-modal id="modal-1" size="lg" scrollable title="Details">
              <b-table
                  responsive
                  stacked
                  :items="modal_items"
                  :fields="modal_fields"
              ></b-table>
            </b-modal>
        </div>
      </b-container>
    </div>
  </VDBaseTemplate>
</template>

<script>
// @ is an alias to /src
import { HTTP } from "@/utilities/http-common";
import HandleErrors from "@/utilities/handle-errors";
import VDBaseTemplate from "@/layouts/VDBaseTemplate.vue";
import { mapState } from "vuex";
import * as _ from "lodash";
export default {
    name: "curate-vectraits",
    mixins: [VDBaseTemplate],
    components: {
        VDBaseTemplate
    },
    data(){
        return{
            apiErrModalShow: false,
            forceCommitModalShow: false,
            confirmRollbackModalShow: false,
            rollbackWorkingModalShow: false,
            viewErrorsModalShow: false,
            viewErrorsLoading: false,
            viewErrorsData: {
              file_details: {},
              error_list: []
            },
            data: [],
            summary: false,
            file_upload_objs: [],
            summary_list: [],
            error_list: [],
            err_msg: '',
            loading: false,
            show_files: true,
            sortBy: "id",
            sortDesc: false,
            deleteDatasetModalShow: false,
            deleteConfirmed: null,
            deleteConfirmState: null,
            selectedFile: null,
            modal_items: [],
            modal_fields: [],
            options: {
                currentPage: 1,
                totalRows: 0,
                perPage: 50
            },
            fields: [
              { key: 'id', sortable: true, tdClass: 'text-truncate vt-table-cell' },
              { key: 'file_name', sortable: true, tdClass: 'text-truncate vt-table-cell' },
              // { key: 'file', sortable: true, tdClass: 'text-truncate vt-table-cell' },
              { key: 'uploader', sortable: true, tdClass: 'text-truncate vt-table-cell' },
              { key: 'status', sortable: true, tdClass: 'text-truncate vt-table-cell' },
              { key: 'created', sortable: true, tdClass: 'text-truncate vt-table-cell' },
              { key: 'modified', sortable: true, tdClass: 'text-truncate vt-table-cell' },
              { key: 'actions', sortable: false, tdClass: 'vt-table-cell-wide' },
            ],
        };
    },
    computed: {
      ...mapState("user", ["user", "myGroups"]),
      is_admin: function() {
        const that = this;
        let groups = ['admin'];
        return Boolean((_.intersection(that.myGroups, groups)).length);
      },
    },
    watch: {
      view: function() {
          this.$refs.table.refresh();
      }
    },
    created() {
      document.title = "Curate VecDyn File Uploads";
    },
    methods: {
      hideModal() {
        this.apiErrModalShow = false;
        this.$refs.table.refresh();
      },
      hideConfirmRollbackModal() {
        this.confirmRollbackModalShow = false;
      },
      showRollbackModal(item) {
        this.selectedFile = item;
        this.confirmRollbackModalShow = true;
      },
      executeRollback() {
        this.rollbackWorkingModalShow = true;
        const fd = new FormData();
        fd.append("file_id", this.selectedFile.id);
        HTTP.post("/api/vecdyn-rollback-file-upload/", fd)
          .then(response => {
            this.rollbackWorkingModalShow = false;
            this.$notify({
              text: response.data.message || "Rollback completed successfully!",
              duration: 10000,
              type: "success",
            });
            // Update only the status of the specific row instead of refreshing the entire table
            this.selectedFile.status = "ROLLBACK";
            // No need to refresh the entire table
            // this.$refs.table.refresh();
          })
          .catch(error => {
            this.rollbackWorkingModalShow = false;
            this.err_msg = HandleErrors.HandleErrors(error);
            this.apiErrModalShow = true;
          });
      },
      /**
       * Fetches and displays the upload errors for a specific file.
       * Opens a modal showing file details and any errors from the upload process.
       * @param {Object} item - The file upload object containing the id to fetch errors for
       */
      viewErrors(item) {
        // Show the modal and set loading state
        this.viewErrorsModalShow = true;
        this.viewErrorsLoading = true;
        // Reset previous data
        this.viewErrorsData = {
          file_details: {},
          error_list: []
        };
        // Fetch errors from the API
        HTTP.get(`/api/vecdyn-upload-errors/${item.id}/`)
          .then(response => {
            this.viewErrorsData = {
              file_details: response.data.file_details || {},
              error_list: response.data.error_list || []
            };
            this.viewErrorsLoading = false;
          })
          .catch(error => {
            this.viewErrorsLoading = false;
            this.viewErrorsModalShow = false;
            this.err_msg = HandleErrors.HandleErrors(error);
            this.apiErrModalShow = true;
          });
      },
      myProvider(ctx) {
          this.$bvModal.hide('modal-1');
          const that = this;

          // build the params object to be sent with the request.
          var params = new URLSearchParams();
          params.append("page", ctx.currentPage)
          params.append("sort_column", that.sortBy)
          params.append("sort_dir", that.sortDesc ? "desc" : "asc")

          var request = {
            params: params
          };

          return HTTP.get(`/api/vecdyn-uploads/`, request)
          .then(response => {
            that.data = response.data.results;
            that.options.totalRows = parseInt(response.data.count);
            return that.data;
          })
          .catch(error => {
            this.err_msg = HandleErrors.HandleErrors(error);
            this.apiErrModalShow = true;
            return [];
          });
        },
        onRowSelected(items) {
            this.$router.push({ path: '/vd-fileupload-detail/' +  items[0]['id']});
        },
        beforeRouteLeave(to, from, next) {
            this.$bvModal.hide('modal-1');
            next();
        },
        toggleFilesTable: function() {
          this.show_files = !this.show_files;
        },
        forceCommitFile(item) {
          this.forceCommitModalShow = true;
          const fd = new FormData();
          fd.append("uploaded_file_id", item.id);
          fd.append("notes", item.notes);
          HTTP.post("/api/vecdyn-force-commit/", fd)
            .then(response => { 
              this.forceCommitModalShow = false;
              this.err_msg = response.data.err_msg;
              this.$notify({
                text: "File committed successfully!",
                duration: 10000,
                type: "success",
              });
              this.$refs.table.refresh();
            })
            .catch((error) => {
              this.err_msg = HandleErrors.HandleErrors(error);
              this.apiErrModalShow = true;
              this.forceCommitModalShow = false;
            });
        },
        naiveCommitFile(item) {
          this.forceCommitModalShow = true;
          const fd = new FormData();
          fd.append("uploaded_file_id", item.id);
          fd.append("notes", item.notes);
          HTTP.post("/api/vecdyn-commit/", fd)
            .then(response => { 
              this.forceCommitModalShow = false;
              this.err_msg = response.data.err_msg;
              this.$notify({
                text: "File committed successfully!",
                duration: 10000,
                type: "success",
              });
              this.$refs.table.refresh();
            })
            .catch((error) => {
              this.err_msg = HandleErrors.HandleErrors(error);
              this.apiErrModalShow = true;
              this.forceCommitModalShow = false;
            });
        },
        rejectFile(item) {
          const fd = new FormData();
          fd.append("uploaded_file_id", item.id);
          fd.append("notes", "Rejected via the Curate VecDyn page");
          HTTP.post("/api/vecdyn-reject/", fd)
          .then(response => { 
            this.err_msg = response.data.err_msg;
            this.$notify({
              text: "File rejected successfully!",
              duration: 10000,
              type: "success",
            });
            // Update the status of the item to "REJECTED"
            item.status = "REJECTED";
          })
          .catch((error) => {
            this.err_msg = HandleErrors.HandleErrors(error);
            this.apiErrModalShow = true;
          });
        },
        showDeleteModal(item) {
          this.deleteDatasetModalShow = true;
          this.selectedFile = item;
        },
        resetDeleteModal() {
          this.deleteConfirmed = ''
          this.deleteConfirmState = null
          this.selectedFile = null
        },
        handleDelete(bvModalEvt){
          bvModalEvt.preventDefault()
          // Exit when the form isn't valid
          if (!this.deleteConfirmed) {
            this.deleteConfirmState = false;
            return;
          }
          // Hide the modal manually
          this.deleteDatasetModalShow = false;
          // Do the delete
          return HTTP.delete(`/api/vecdyn-file-delete/${this.selectedFile.id}/`)
          .then(response => {
            // Refresh the table to show updated data
            this.$refs.table.refresh();
            
            // Show success notification
            this.$notify({
              text: "File deleted successfully!",
              duration: 10000,
              type: "success",
            });
            
            return response;
          })
          .catch(error => {
            this.err_msg = HandleErrors.HandleErrors(error);
            this.apiErrModalShow = true;
            return [];
          });
        }
    },
};
</script>

<style scoped>
  h1, h2, h3, h4, a, p, text, table {
      color: black !important;
      text-shadow: none;
  }

  li {
    display: list-item !important;
    list-style-position: outside !important;
  }

  .table-head {
    background: #777;
    font-weight: bold;
    text-align: left;
    padding: 5px;
  }

  .btn-group-xs > .btn, .btn-xs {
    margin-left: 15px;
    padding: .25rem .4rem;
    font-size: .875rem;
    line-height: .5;
    border-radius: .2rem;
  }
</style>